import { motion } from "framer-motion";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const item = {
  hidden: { opacity: 0, y: 32 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

// Deliberate bento shape — aligned 12-col grid.
// Spans tuned so a 6-service list fills a clean, non-blocky layout:
//   row 1: feature(7) + card(5)
//   row 2: card(5) + card(7)
//   row 3: card(4) + card(4) + card(4)
const SPANS = [
  "md:col-span-7 md:row-span-2 md:min-h-[520px]",
  "md:col-span-5 md:min-h-[250px]",
  "md:col-span-5 md:min-h-[250px]",
  "md:col-span-7 md:min-h-[250px]",
  "md:col-span-4 md:min-h-[280px]",
  "md:col-span-4 md:min-h-[280px]",
  "md:col-span-4 md:min-h-[280px]",
];

export default function ServicesBento({ site }) {
  const services = site.services;

  return (
    <section
      id="services"
      data-testid="care-services"
      className="relative py-24 md:py-32 bg-[#FDFBF7]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-2xl">
            <span className="eyebrow">What we do</span>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight mt-5 leading-[1.03] text-[#1A2621]">
              Care that meets
              <span className="italic text-[#284A3B]"> your family</span> exactly
              where it is.
            </h2>
          </div>
          <p className="max-w-md text-[#4A5D54] leading-relaxed">
            Every care plan is built around the person — never a template.
            Choose the service you need; we&rsquo;ll match the caregiver.
          </p>
        </div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6"
        >
          {services.map((s, i) => (
            <motion.article
              key={s.key}
              variants={item}
              data-testid={`care-service-${s.key}`}
              className={`relative group overflow-hidden rounded-2xl border border-[#D5DACF] bg-white ${SPANS[i] || "md:col-span-4 md:min-h-[260px]"}`}
            >
              <img
                src={s.image}
                alt={s.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A2621]/85 via-[#1A2621]/35 to-transparent" />
              {/* Soft spotlight */}
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#C86A51]/15 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

              <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-8 text-[#FDFBF7]">
                <div className="text-[11px] uppercase tracking-[0.26em] text-[#FDFBF7]/80">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl leading-tight mt-2">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm md:text-base text-[#FDFBF7]/85 max-w-md leading-relaxed">
                  {s.desc}
                </p>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
